import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
  PictureAsPdf as PictureAsPdfIcon,
  CloudDownload as CloudDownloadIcon,
  AddOutlined,
} from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../../config";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";

export function VehicleDocumentManager({
  vehicleId,
  documents: propDocuments, // Use the documents prop passed from VehicleAddMain
  setDocuments: setPropDocuments, // Use the setter for documents from VehicleAddMain
  isEditing,
  onComplete,
  orgId,
  onSubmit,
  onCancel,
  isDetailsFormValid,
}) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([
    {
      id: 1,
      type: "",
      file: null,
      fileName: "",
      preview: null,
      isExisting: false,
      isUploaded: false,
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    documentId: null,
  });

  const documentTypes = [
    "RC Book",
    "Emission Test Report",
    "Insurance Policy",
    "Other",
  ];
  const specificTypes = ["RC Book", "Emission Test Report", "Insurance Policy"];
  const maxOtherDocuments = 3;

  useEffect(() => {
    if (isEditing && vehicleId && orgId) {
      fetchExistingDocuments();
    }
    return () => {
      documents.forEach((doc) => {
        if (doc.preview && !doc.isExisting) {
          URL.revokeObjectURL(doc.preview);
        }
      });
    };
  }, [vehicleId, isEditing, orgId]);

  const filterMostRecentDocuments = (docs) => {
    // Group documents by name/type
    const groupedDocs = docs.reduce((acc, doc) => {
      const type = doc.name || doc.type || "Other";
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(doc);
      return acc;
    }, {});

    // For each group, sort by createdAt (descending) and take the most recent
    const filteredDocs = Object.values(groupedDocs).map(group => {
      return group.sort((a, b) => new Date(b.createdAt || b.updatedAt) - new Date(a.createdAt || a.updatedAt))[0];
    });

    return filteredDocs;
  };

  const fetchExistingDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/vehicle/id`, {
        params: { vehicleId, organizationId: orgId },
        withCredentials: true,
      });

      const vehicleData = response.data.vehicle || {};
      const vehicleDocs = vehicleData.documents || [];

      // Filter out VehicleImage (already handled by VehicleAddMain)
      const nonImageDocs = vehicleDocs.filter(doc => doc.name !== "VehicleImage");

      // Filter to keep only the most recent document for each type
      const filteredDocs = filterMostRecentDocuments(nonImageDocs);

      if (Array.isArray(filteredDocs) && filteredDocs.length > 0) {
        const existingDocs = filteredDocs.map((doc, index) => ({
          id: doc.id || index + 1,
          type: doc.name || "Other",
          fileName: doc.location ? doc.location.split("/").pop() : "",
          fileId: doc.id,
          isExisting: true,
          isUploaded: true,
          createdAt: doc.createdAt,
          updatedAt: doc.updatedAt,
          preview: doc.location
            ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(doc.location)}`
            : null,
        }));

        setDocuments(existingDocs);
        setPropDocuments(existingDocs); // Sync with parent
      } else {
        setAlert({
          open: true,
          message: t("No documents found for this vehicle"),
          severity: "info",
        });
        setDocuments([
          {
            id: 1,
            type: "",
            file: null,
            fileName: "",
            preview: null,
            isExisting: false,
            isUploaded: false,
          },
        ]);
        setPropDocuments([]); // Sync with parent
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setAlert({
        open: true,
        message: t("Failed to load existing documents"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateDocument = (doc) => {
    const newErrors = {};
    if (!doc.type) {
      newErrors.type = t("validation.required", { field: t("Document Type") });
    }
    if (!doc.file && !doc.isUploaded) {
      newErrors.file = t("validation.required", { field: t("Document File") });
    }
    return newErrors;
  };

  const validateAllDocuments = () => {
    const newErrors = {};
    documents.forEach((doc) => {
      if (touched[doc.id]) {
        newErrors[doc.id] = validateDocument(doc);
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).every(
      (id) => Object.keys(newErrors[id]).length === 0
    );
  };

  useEffect(() => {
    validateAllDocuments();
  }, [documents, touched]);

  const getDocumentCounts = () => {
    const typeCounts = {};
    specificTypes.forEach((type) => {
      typeCounts[type] = 0;
    });
    typeCounts["Other"] = 0;

    documents.forEach((doc) => {
      if ((doc.isUploaded || doc.file) && doc.type) {
        typeCounts[doc.type] = (typeCounts[doc.type] || 0) + 1;
      }
    });

    return typeCounts;
  };

  const canAddDocument = () => {
    const typeCounts = getDocumentCounts();
    const otherCount = typeCounts["Other"] || 0;
    const hasRemainingOtherSlots = otherCount < maxOtherDocuments;
    const hasAvailableSpecificTypes = specificTypes.some(
      (type) => !typeCounts[type]
    );
    const hasEmptyForm = documents.some(
      (doc) => !doc.type && !doc.file && !doc.isUploaded
    );

    return (
      (hasRemainingOtherSlots || hasAvailableSpecificTypes) && !hasEmptyForm
    );
  };

  const getRemainingOtherDocs = () => {
    const typeCounts = getDocumentCounts();
    return maxOtherDocuments - (typeCounts["Other"] || 0);
  };

  const getAvailableDocTypes = (currentDocId) => {
    const doc = documents.find((d) => d.id === currentDocId);
    const usedSpecificTypes = documents
      .filter(
        (d) =>
          specificTypes.includes(d.type) &&
          (d.isUploaded || d.file) &&
          d.id !== currentDocId
      )
      .map((d) => d.type);

    return documentTypes.filter((type) => {
      if (type === "Other" || (doc && doc.type === type)) {
        return true;
      }
      return !usedSpecificTypes.includes(type);
    });
  };

  const handleAddDocument = () => {
    if (!canAddDocument()) {
      setAlert({
        open: true,
        message: t("Cannot add more documents"),
        severity: "warning",
      });
      return;
    }
    const newId =
      documents.length > 0
        ? Math.max(...documents.map((doc) => doc.id)) + 1
        : 1;
    const newDoc = {
      id: newId,
      type: "",
      file: null,
      fileName: "",
      preview: null,
      isExisting: false,
      isUploaded: false,
    };
    setDocuments([...documents, newDoc]);
    setPropDocuments(prev => [...prev, newDoc]); // Sync with parent
  };

  const handleRemoveDocument = (id) => {
    const docToRemove = documents.find((doc) => doc.id === id);

    if (docToRemove.isExisting) {
      setConfirmModal({
        open: true,
        documentId: id,
      });
      return;
    }

    removeDocumentById(id);
  };

  const handleCancelRemove = () => {
    setConfirmModal({ open: false, documentId: null });
  };

  const handleConfirmRemove = async () => {
    const docId = confirmModal.documentId;
    const docToRemove = documents.find((doc) => doc.id === docId);

    if (docToRemove.isExisting && docToRemove.fileId) {
      try {
        await axios.delete(`${config.baseURL}/upload/image`, {
          params: { fileId: docToRemove.fileId },
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error deleting document:", error);
        setAlert({
          open: true,
          message: t("Failed to delete document"),
          severity: "error",
        });
        return;
      }
    }

    removeDocumentById(docId);
    setConfirmModal({ open: false, documentId: null });
  };

  const removeDocumentById = (id) => {
    const docToRemove = documents.find((doc) => doc.id === id);

    if (docToRemove.preview && !docToRemove.isExisting) {
      URL.revokeObjectURL(docToRemove.preview);
    }

    const newDocuments = documents.filter((doc) => doc.id !== id);
    setDocuments(
      newDocuments.length > 0
        ? newDocuments
        : [
            {
              id: Date.now(),
              type: "",
              file: null,
              fileName: "",
              preview: null,
              isExisting: false,
              isUploaded: false,
            },
          ]
    );
    setPropDocuments(
      newDocuments.length > 0
        ? newDocuments
        : []
    ); // Sync with parent
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
    setTouched((prev) => {
      const newTouched = { ...prev };
      delete newTouched[id];
      return newTouched;
    });
  };

  const handleDocTypeChange = (id, value) => {
    const existingSpecificDoc = documents.find(
      (doc) =>
        doc.type === value &&
        specificTypes.includes(value) &&
        (doc.isUploaded || doc.file) &&
        doc.id !== id
    );

    if (existingSpecificDoc && specificTypes.includes(value)) {
      setAlert({
        open: true,
        message: t(`A ${value} document already exists`),
        severity: "warning",
      });
      return;
    }

    if (value === "Other") {
      const currentOtherCount = documents.filter(
        (doc) =>
          doc.type === "Other" && (doc.isUploaded || doc.file) && doc.id !== id
      ).length;

      if (currentOtherCount >= maxOtherDocuments) {
        setAlert({
          open: true,
          message: t(`Maximum ${maxOtherDocuments} Other documents allowed`),
          severity: "warning",
        });
        return;
      }
    }

    const updatedDocs = documents.map((doc) =>
      doc.id === id ? { ...doc, type: value } : doc
    );
    setDocuments(updatedDocs);
    setPropDocuments(updatedDocs.filter(doc => doc.type !== "")); // Sync with parent, exclude empty types
    setTouched((prev) => ({
      ...prev,
      [id]: { ...prev[id], type: true },
    }));
    setErrors((prev) => ({
      ...prev,
      [id]: validateDocument({
        ...documents.find((doc) => doc.id === id),
        type: value,
      }),
    }));
  };

  const handleFileUpload = (id, event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      if (file.size > 10 * 1024 * 1024) {
        setAlert({
          open: true,
          message: t("File size exceeds 10MB limit"),
          severity: "warning",
        });
        return;
      }

      const fileUrl = URL.createObjectURL(file);

      const updatedDocs = documents.map((doc) => {
        if (doc.id === id) {
          if (doc.preview && !doc.isExisting) {
            URL.revokeObjectURL(doc.preview);
          }
          return {
            ...doc,
            file,
            fileName: file.name,
            fileType: file.type,
            preview: fileUrl,
            isUploaded: false,
            needsReplacement: doc.isExisting ? true : false,
          };
        }
        return doc;
      });
      setDocuments(updatedDocs);
      setPropDocuments(updatedDocs.filter(doc => doc.type !== "")); // Sync with parent

      setTouched((prev) => ({
        ...prev,
        [id]: { ...prev[id], file: true },
      }));

      setErrors((prev) => ({
        ...prev,
        [id]: validateDocument({
          ...documents.find((doc) => doc.id === id),
          file,
          fileName: file.name,
          preview: fileUrl,
          isUploaded: false,
        }),
      }));

      event.target.value = "";
    }
  };

  const handleDownload = (doc) => {
    if (doc.preview) {
      const link = document.createElement("a");
      link.href = doc.preview;
      link.download = doc.fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSubmitDocuments = async (vId) => {
    if (!vId) {
      setAlert({
        open: true,
        message: t("Vehicle ID is required to upload documents"),
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      for (const doc of documents) {
        if (doc.file && !doc.isUploaded && doc.type) {
          const formData = new FormData();
          formData.append("image", doc.file);

          if (doc.isExisting && doc.fileId) {
            try {
              await axios.delete(`${config.baseURL}/upload/image`, {
                params: { fileId: doc.fileId },
                withCredentials: true,
              });
              console.log(
                `Successfully deleted document with ID: ${doc.fileId}`
              );
            } catch (deleteError) {
              console.warn("Failed to delete old document:", deleteError);
            }
          }

          const response = await axios.post(
            `${config.baseURL}/upload/image?name=${encodeURIComponent(
              doc.type || "Other"
            )}&associateEntityId=${vId}&associatedWith=Vehicle`,
            formData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            }
          );

          const updatedDoc = {
            ...doc,
            isUploaded: true,
            isExisting: true,
            needsReplacement: false,
            fileName: response.data.location.split("/").pop(),
            fileId: response.data.id,
            preview: `${config.baseURL}/upload/image?fileName=${encodeURIComponent(response.data.location)}`,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          // Update documents, replacing any existing document of the same type
          const updatedDocs = documents.map(d =>
            d.id === doc.id ? updatedDoc : d
          ).filter(d => d.type !== doc.type || d.id === doc.id);
          
          setDocuments(updatedDocs);
          setPropDocuments(updatedDocs.filter(d => d.type !== "")); // Sync with parent
        }
      }

      setAlert({
        open: true,
        message: t("Documents uploaded successfully"),
        severity: "success",
      });
      if (canAddDocument()) {
        handleAddDocument();
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      setAlert({
        open: true,
        message: t("Failed to upload documents"),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    if (!isDetailsFormValid) {
      setAlert({
        open: true,
        message: t("Please correct all validation errors before submitting"),
        severity: "error",
      });
      return;
    }

    const isValid = validateAllDocuments();
    if (!isValid) {
      setAlert({
        open: true,
        message: t("Please correct all document validation errors"),
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit();
      const currentVehicleId = result || vehicleId;

      if (!currentVehicleId) {
        setAlert({
          open: true,
          message: t("Failed to save vehicle details"),
          severity: "error",
        });
        setIsSubmitting(false);
        return;
      }

      const docsToUpload = documents.filter(
        (doc) => doc.file && !doc.isUploaded && doc.type
      );
      if (docsToUpload.length > 0) {
        await handleSubmitDocuments(currentVehicleId);
      }

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error("Error during save process:", error);
      setAlert({
        open: true,
        message: t("Failed to save. Please try again."),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFilePreview = (doc) => {
    if (!doc.preview) return null;

    const isPdf =
      doc.fileType === "application/pdf" ||
      (doc.fileName && doc.fileName.toLowerCase().endsWith(".pdf"));

    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {isPdf ? (
          <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            <object
              data={doc.preview}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            >
              <Box sx={{ textAlign: "center", p: 2 }}>
                <PictureAsPdfIcon sx={{ fontSize: 48, color: "#F44336" }} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t("PDF Preview Not Available")}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={() => window.open(doc.preview, "_blank")}
                >
                  {t("View PDF")}
                </Button>
              </Box>
            </object>
          </Box>
        ) : (
          <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
            <img
              src={doc.preview}
              alt="Document preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
            />
          </Box>
        )}
        {doc.isUploaded && !doc.needsReplacement && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: 8,
              left: 0,
              right: 0,
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              padding: "4px 8px",
              color: "green",
              borderRadius: "4px",
            }}
          >
            ✓ {t("Uploaded")}
          </Typography>
        )}
        {doc.needsReplacement && (
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              bottom: 8,
              left: 0,
              right: 0,
              textAlign: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              padding: "4px 8px",
              color: "orange",
              borderRadius: "4px",
            }}
          >
            {t("Pending replacement")}
          </Typography>
        )}
      </Box>
    );
  };

  const errorTextStyles = {
    color: "#d32f2f",
    fontSize: "0.75rem",
    mt: 0.5,
    ml: 1.5,
  };

  return (
    <Box
      sx={{
        padding: { xs: "20px", md: "40px" },
        margin: { xs: "0px 20px", md: "0px 40px 0px 40px" },
        backgroundColor: "#FFFFFF",
        border: "1px solid #A5DCF3",
        borderRadius: "10px",
        height: "100%",
        boxShadow: "-2px 2px 5px 0px #00000040",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 4,
        }}
      >
        <Box sx={{ mb: { xs: 2, md: 0 } }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: "bold", color: "#000" }}
          >
            {t("Upload Documents")} ({t("RC Book")}, {t("Emission Test Report")}
            , {t("Insurance Policy")})
            <Typography
              variant="body2"
              component="span"
              color="error"
              sx={{ ml: 0.5 }}
            >
              *
            </Typography>
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {documents.map((doc) => (
            <Grid item xs={12} sm={4} key={doc.id}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  border: "1px solid #183059",
                  borderRadius: "8px",
                  padding: 2,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    mb: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FormControl
                    fullWidth
                    error={!!(touched[doc.id]?.type && errors[doc.id]?.type)}
                    sx={{ maxWidth: "300px" }}
                  >
                    <Select
                      value={doc.type}
                      onChange={(e) =>
                        handleDocTypeChange(doc.id, e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [doc.id]: { ...prev[doc.id], type: true },
                        }))
                      }
                      disabled={
                        doc.isExisting &&
                        doc.isUploaded &&
                        !doc.needsReplacement
                      }
                      displayEmpty
                      sx={{
                        borderRadius: "8px",
                        height: "40px",
                        backgroundColor: "#B0BEC5",
                        color: "#000",
                        "& .MuiSelect-select": {
                          padding: "8px 14px",
                          display: "flex",
                          alignItems: "center",
                        },
                        "& svg": {
                          color: "#000",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t("Select a document type")}
                      </MenuItem>
                      {getAvailableDocTypes(doc.id).map((type) => {
                        const isTypeUsedElsewhere =
                          specificTypes.includes(type) &&
                          documents.some(
                            (d) =>
                              d.id !== doc.id &&
                              d.type === type &&
                              (d.isUploaded || d.file)
                          );

                        return (
                          <MenuItem
                            key={type}
                            value={type}
                            disabled={isTypeUsedElsewhere && doc.type !== type}
                          >
                            {t(type)}
                            {isTypeUsedElsewhere && doc.type !== type && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1 }}
                              >
                                {t("(Already uploaded)")}
                              </Typography>
                            )}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {touched[doc.id]?.type && errors[doc.id]?.type && (
                      <FormHelperText sx={errorTextStyles}>
                        {errors[doc.id].type}
                      </FormHelperText>
                    )}
                  </FormControl>
                  {doc.preview && (
                    <IconButton
                      onClick={() => handleDownload(doc)}
                      disabled={isSubmitting}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid #ccc",
                        width: 40,
                        height: 40,
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <ArrowDownwardOutlinedIcon sx={{ color: "#1976D2" }} />
                    </IconButton>
                  )}
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    height: "300px",
                    border: errors[doc.id]?.file
                      ? "1px dashed #d32f2f"
                      : "1px solid #183059",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    mb: 1,
                  }}
                >
                  {doc.preview ? (
                    renderFilePreview(doc)
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 3,
                        textAlign: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <AddOutlined sx={{ fontSize: 32 }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {t("New Document")}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          backgroundColor: "#20A8E0",
                          "&:hover": { backgroundColor: "#1565C0" },
                          py: 1,
                          px: 3,
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "14px",
                        }}
                        startIcon={<UploadFileIcon sx={{ fontSize: "16px" }} />}
                        onClick={() =>
                          document.getElementById(`file-upload-${doc.id}`).click()
                        }
                      >
                        {t("Upload")}
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                        {t("Supported formats: Images, PDF (Max 10MB)")}
                      </Typography>
                      <input
                        id={`file-upload-${doc.id}`}
                        type="file"
                        accept="image/*,application/pdf"
                        hidden
                        onChange={(e) => handleFileUpload(doc.id, e)}
                      />
                    </Box>
                  )}
                </Box>
                {doc.preview && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 1,
                      mt: 1,
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        backgroundColor: "#20A8E0",
                        "&:hover": { backgroundColor: "#1565C0" },
                        py: 1.5,
                        px: 3,
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "16px",
                        minWidth: "120px",
                      }}
                      startIcon={<UploadFileIcon sx={{ fontSize: "16px" }} />}
                      component="label"
                      disabled={isSubmitting}
                    >
                      {t("Change")}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        hidden
                        key={doc.id}
                        onChange={(e) => handleFileUpload(doc.id, e)}
                      />
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        backgroundColor: "#F44336",
                        "&:hover": { backgroundColor: "#D32F2F" },
                        py: 1.5,
                        px: 3,
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "16px",
                        minWidth: "120px",
                      }}
                      startIcon={<DeleteIcon sx={{ fontSize: "16px" }} />}
                      onClick={() => handleRemoveDocument(doc.id)}
                      disabled={isSubmitting}
                    >
                      {t("Delete")}
                    </Button>
                  </Box>
                )}
                {touched[doc.id]?.file && errors[doc.id]?.file && (
                  <FormHelperText sx={errorTextStyles}>
                    {errors[doc.id].file}
                  </FormHelperText>
                )}
              </Box>
            </Grid>
          ))}
          {canAddDocument() && (
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  height: 350,
                  border: "2px dashed #1976D2",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#E3F2FD",
                  cursor: "pointer",
                }}
                onClick={handleAddDocument}
              >
                <Box sx={{ textAlign: "center" }}>
                  <AddIcon sx={{ fontSize: 48, color: "#1976D2", mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {t("Add New Document")}
                  </Typography>
                  {getRemainingOtherDocs() > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, color: "#1976D2" }}
                    >
                      {t(
                        `${getRemainingOtherDocs()} Other document${
                          getRemainingOtherDocs() !== 1 ? "s" : ""
                        } remaining`
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          marginTop: "40px",
        }}
      >
        <Button
          onClick={onCancel}
          sx={{
            width: 120,
            fontSize: "16px",
            color: "#1F242E",
            height: 40,
            borderRadius: "8px",
            textTransform: "none",
            padding: "8px 24px",
            background: "#E0E0E0",
            border: "1px solid #414958",
            fontWeight: "bold",
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSubmitting || !isDetailsFormValid}
          sx={{
            width: 120,
            height: 40,
            fontSize: "16px",
            color: "#1F242E",
            textTransform: "none",
            borderRadius: "8px",
            padding: "8px 24px",
            background: "#F9E719",
            border: "1px solid #414958",
            fontWeight: "bold",
            "&:disabled": {
              background: "#E0E0E0",
              color: "#A0A0A0",
              border: "1px solid #A0A0A0",
            },
          }}
        >
          {isSubmitting ? `${t("Submitting")}...` : t("Save")}
        </Button>
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={confirmModal.open}
        onClose={handleCancelRemove}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "-2px 2px 5px 0px #00000040",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ bgcolor: "#F2F2F3", color: "#414958" }}
        >
          {t("Confirm Document Removal")}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <DialogContentText id="alert-dialog-description">
            {t(
              "Are you sure you want to remove this document? This action cannot be undone."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelRemove}
            sx={{
              color: "#1F242E",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              background: "#F2F2F3",
              border: "1px solid #414958",
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleConfirmRemove}
            sx={{
              color: "#FFFFFF",
              borderRadius: "8px",
              textTransform: "none",
              padding: "8px 16px",
              background: "#d32f2f",
              "&:hover": { background: "#b71c1c" },
            }}
            autoFocus
          >
            {t("Remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}